import React, { useCallback, useContext, useMemo } from 'react'

import styled from 'styled-components';

import CIRCLE_SWITCH from '../elements/CircleSwitch';
import { DataContext } from '../contexts/DataContext';
import { MAX_VISIBLE_ISSUES, STATIC_URL } from '../lib/settings';
import { useSwiper } from 'swiper/react';
import { useDevice } from '../contexts/DeviceContext';

export default function IssueIntro({issueNum, date, title, annotation, imgSrc, disableSwitches = false}) {
  const { currentIssueIndex, setCurrentIssueIndex, issues, issueIds, getIssue } = useContext(DataContext);
  const { isMobile } = useDevice();

  const swiper = useSwiper();

  const latestIssueIds = useMemo(() => issueIds?.slice(-MAX_VISIBLE_ISSUES).reverse() || [], [issueIds]);

  const onClickSwitch = useCallback(async (id) => {
    const currentIssueIndex = issueIds.indexOf(id);
    if (issues[id]) setCurrentIssueIndex(currentIssueIndex)
    else {
      await getIssue(id);
      setCurrentIssueIndex(currentIssueIndex);
    }
    if (isMobile) swiper.slideTo(latestIssueIds.indexOf(id));
  }, [issueIds, getIssue, issues, setCurrentIssueIndex, swiper, latestIssueIds, isMobile]);

  return (
    <ISSUE_INTRO>
      <ISSUE_TOP>
        <ISSUE_NUMBER>
          <span>{issueNum}</span>
          <span className='dot' />
          <span>{date}</span>
        </ISSUE_NUMBER>

        {!disableSwitches && issueIds && issueIds.length > 1 && latestIssueIds?.includes(issueIds[currentIssueIndex]) ? (
          <div className="circle-switches">
            {latestIssueIds.map((id, index) => (
              <CIRCLE_SWITCH
                key={index}
                $filled={id === issueIds[currentIssueIndex]}
                onClick={() => onClickSwitch(id)}
              />
            ))}
          </div>
        ) : null}

        <h1>{title}</h1>
      </ISSUE_TOP>
      <ISSUE_MAIN>
        <img src={`${STATIC_URL}/${imgSrc}`} alt="Обложка номера" />
        <div className="annotation">
          {annotation.split('\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      </ISSUE_MAIN>
    </ISSUE_INTRO>
  )
}

const ISSUE_NUMBER = styled.section`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 0.5rem;
  order: 1;

  font-size: 0.75rem;
  font-weight: 700;
  font-family: var(--primary-font);

  & > .dot {
    display: inline-block;
    width: 0.2rem;
    height: 0.2rem;
    border-radius: 50%;
    background-color: black;
  }

  @media (min-width: 768px) {
    order: 1;
    flex: 0 0 100%;

    font-size: 1.25rem;
  }
`;

const ISSUE_TOP = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  & > h1 {
    order: 3;
    flex: 0 0 100%;
    font-size: 1.5rem;
    font-weight: 700;
    font-family: var(--primary-font);
  }

  & .circle-switches {
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;
    order: 2
  }

  @media (min-width: 768px) {
    & > h1 {
      flex: 0 0 auto;
      order: 2;
      font-size: 2.75rem;
    }

    & .circle-switches {
      order: 3;
    }
  }
`;

const ISSUE_MAIN = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  gap: 1.25rem;

  & > img {
    width: 100%;
  }

  & .annotation {
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    align-self: start;
  }

  & p {
    font: var(--main-text-font);
    min-height: 1rem;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: start;
    align-items: start;
    gap: 1.25rem;

    & > img {
      width: 32%;
    }

    & p {
      font-size: 1.25rem;
    }
  }
`;

const ISSUE_INTRO = styled.section`
  margin: 1.25rem 20px 7.5rem 20px;
  
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (min-width: 768px) {
    margin: 4.5rem var(--main-side-padding) 100px var(--main-side-padding);
    margin-top: 4.5rem;

    gap: 1.25rem;
  }
`;

